import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Box, Button, Chip, makeStyles, Typography, useTheme } from '@material-ui/core'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  EnumEstadoCiclo,
  EnumEstadoFactura,
  EnumSubtipoSujeto,
  EnumTipoContrato,
  EnumTipoFacturacion
} from '../../../../api'
import { EnumDominioTarea } from '../../../../api/oData/enums/enumDominioTarea'
import { CardWithTransition } from '../../../../components/shared/cardWithTransition/CardWithTransition'
import { enumToSelectOptionArray } from '../../../../helpers'
import {
  ALLOWED_QUERY_REGEXP,
  ALLOWED_QUERY_REGEXP_STR
} from '../../../../helpers/sanitizeQueryHelper'
import { getCustomerDomain } from '../../../../redux'
import { listObjDescriptionsItemMapper } from '../../../invoicesPage/invoicePage.mapper'
import { fetchObjectionsDescriptions } from '../../../invoicesPage/invoicesPage.service'
import { EnumSettlementSearchType, SettlementFormSearchFilter } from '../../settlements.models'
import { searchFilterCardStyle } from './searchFilterCard.styles'

interface SearchFilterCardProps {
  showCard: boolean
  filters: SettlementFormSearchFilter
  cycleState: EnumEstadoCiclo
  advancedSearch?: boolean
  onSearch: (formState: SettlementFormSearchFilter) => void
  onCancel: () => void
  billingType?: EnumTipoFacturacion
}

export const SearchFilterCard: React.FC<SearchFilterCardProps> = ({
  showCard,
  filters,
  cycleState = EnumEstadoCiclo.PdteValidarListado,
  advancedSearch = false,
  billingType,
  onSearch,
  onCancel
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'settlements', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(searchFilterCardStyle(currentTheme))
  const classes = useStyles()
  const { error } = useNotify()

  const initialFormState: SettlementFormSearchFilter = {
    ...filters
  }

  const [searchFormData, setSearchFormData] = useState<SettlementFormSearchFilter>(initialFormState)
  const [isLoadingDescriptions, setIsLoadingDescriptions] = useState<boolean>(false)
  const [descriptionsOptions, setDescriptionsOptions] = useState<SelectOption[]>([])

  const isRefacturacionOrAnuladora =
    billingType === EnumTipoFacturacion.Refacturacion ||
    billingType === EnumTipoFacturacion.Anuladora

  // #region Handlers Starts

  const handleInputChange = (fieldName: string, value: any) => {
    setSearchFormData(prevState => ({
      ...prevState,
      [fieldName]: value
    }))
  }

  const handleSearchTypeChange = (searchType: EnumSettlementSearchType) => {
    setSearchFormData(prevState => ({
      ...prevState,
      searchType: searchType
    }))
  }

  const handleCleanFilters = () => {
    setSearchFormData({
      searchType: EnumSettlementSearchType.Contract,
      searchValue: ''
    })
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  const hasAdvancedFiltersApplied = (): boolean => {
    if (!advancedSearch) return false

    return !!(
      searchFormData.hasInvoiceObjections ||
      searchFormData.hasNotInvoiceObjections ||
      searchFormData.invoiceState ||
      searchFormData.amountFrom ||
      searchFormData.amountTo ||
      searchFormData.issueDateFrom ||
      searchFormData.issueDateTo ||
      searchFormData.subjectSubtype ||
      searchFormData.contractType ||
      searchFormData.masterObjectionId
    )
  }

  const isSearchDisabled = (): boolean => {
    const raw = searchFormData.searchValue || ''
    const trimmed = raw.trim()
    const hasLowQualityBad = searchFormData.lowQualityBad === true
    const hasAdvancedFilters = hasAdvancedFiltersApplied()

    if (raw && !ALLOWED_QUERY_REGEXP.test(raw)) return true
    if (raw && trimmed.length < 3) return true
    if (!trimmed && !hasAdvancedFilters && !hasLowQualityBad) return true

    return false
  }
  // #endregion Functions Ends

  // #region Form Definition Starts
  const searchFormOptions: FormOptions = {
    elevation: 0,
    columns: 1,
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlements:components.searchFilterCard.analytics')
    }
  }

  const customSearchFormSections = useMemo(() => {
    const sections: Section[] = [
      {
        title: undefined,
        inputs: [
          {
            name: 'searchValue',
            label: t('settlements:components.searchFilterCard.inputs.searchValue'),
            type: DataType.TEXT,
            clearable: true,
            helpText: t('settlements:components.searchFilterCard.helpText.allowedChars'),
            errorText: t('settlements:components.searchFilterCard.helpText.allowedChars'),
            regexp: ALLOWED_QUERY_REGEXP_STR,
            onChange: value => handleInputChange('searchValue', value)
          },
          {
            name: 'lowQualityBad',
            label: t('settlements:components.searchFilterCard.inputs.lowQualityBad'),
            type: DataType.SWITCH,
            inputSize: 12,
            onChange: value => handleInputChange('lowQualityBad', value ? true : undefined)
          }
        ]
      }
    ]

    // Advanced search filters section (for settlements invoices)
    if (advancedSearch) {
      sections.push({
        title: t('settlements:components.searchFilterCard.advancedFilters'),
        collapsible: true,
        inputs: [
          {
            name: 'hasInvoiceObjections',
            label: t('settlements:components.searchFilterCard.inputs.hasInvoiceObjections'),
            type: DataType.SWITCH,
            inputSize: 6,
            onChange: value => {
              // If activating "hasInvoiceObjections", deactivate "hasInvoiceObjections". Argentia API expects true for objections filter and undefined for no filter.
              handleInputChange('hasInvoiceObjections', value ? true : undefined)
              handleInputChange('hasNotInvoiceObjections', value ? false : undefined)
            }
          },
          {
            name: 'hasNotInvoiceObjections',
            label: t('settlements:components.searchFilterCard.inputs.hasNotInvoiceObjections'),
            type: DataType.SWITCH,
            inputSize: 6,
            onChange: value => {
              // If activating "hasNotInvoiceObjections", deactivate "hasInvoiceObjections". Argentia API expects false for no objections filter and undefined for no filter.
              handleInputChange('hasInvoiceObjections', value ? false : undefined)
            }
          },
          {
            name: 'masterObjectionId',
            label: t('settlements:components.searchFilterCard.inputs.objDesc'),
            type: DataType.SELECT,
            selectOptions: descriptionsOptions,
            clearable: true,
            inputSize: 12,
            onChange: value => handleInputChange('masterObjectionId', value)
          },
          {
            name: 'invoiceState',
            label: t('settlements:components.searchFilterCard.inputs.invoiceState'),
            type: DataType.SELECT,
            clearable: true,
            selectOptions: enumToSelectOptionArray(EnumEstadoFactura, 'enums:EnumEstadoFactura'),
            inputSize: 12,
            onChange: value => handleInputChange('invoiceState', value)
          },
          {
            name: 'amountFrom',
            label: t('settlements:components.searchFilterCard.inputs.amountFrom'),
            type: DataType.NUMBER,
            clearable: true,
            inputSize: 6,
            placeholder: '0,00',
            onChange: value => handleInputChange('amountFrom', value)
          },
          {
            name: 'amountTo',
            label: t('settlements:components.searchFilterCard.inputs.amountTo'),
            type: DataType.NUMBER,
            clearable: true,
            inputSize: 6,
            onChange: value => handleInputChange('amountTo', value)
          },
          {
            name: 'issueDateFrom',
            label: t('settlements:components.searchFilterCard.inputs.issueDateFrom'),
            type: DataType.DATE,
            clearable: true,
            inputSize: 6,
            onChange: value => handleInputChange('issueDateFrom', value)
          },
          {
            name: 'issueDateTo',
            label: t('settlements:components.searchFilterCard.inputs.issueDateTo'),
            type: DataType.DATE,
            clearable: true,
            inputSize: 6,
            onChange: value => handleInputChange('issueDateTo', value)
          },
          {
            name: 'subjectSubtype',
            label: t('settlements:components.searchFilterCard.inputs.subjectSubtype'),
            type: DataType.SELECT,
            clearable: true,
            inputSize: 6,
            selectOptions: enumToSelectOptionArray(EnumSubtipoSujeto, 'enums:EnumSubtipoSujeto'),
            onChange: value => handleInputChange('subjectSubtype', value)
          },
          {
            name: 'contractType',
            label: t('settlements:components.searchFilterCard.inputs.contractType'),
            type: DataType.SELECT,
            clearable: true,
            inputSize: 6,
            selectOptions: enumToSelectOptionArray(EnumTipoContrato, 'enums:EnumTipoContrato'),
            onChange: value => handleInputChange('contractType', value)
          }
        ]
      })
    }

    return sections
  }, [descriptionsOptions, cycleState, advancedSearch])

  // #endregion Form Definition Ends

  const searchTypeOptions = [
    {
      searchType: EnumSettlementSearchType.Contract,
      label: t('settlements:components.searchFilterCard.searchTypes.contract'),
      disabled: false
    },
    {
      searchType: EnumSettlementSearchType.CUPS,
      label: t('settlements:components.searchFilterCard.searchTypes.cups')
    },
    {
      searchType: EnumSettlementSearchType.Subject,
      label: t('settlements:components.searchFilterCard.searchTypes.subject')
    },
    {
      searchType: EnumSettlementSearchType.Trade,
      label: t('settlements:components.searchFilterCard.searchTypes.trade')
    },
    {
      searchType: EnumSettlementSearchType.Product,
      label: t('settlements:components.searchFilterCard.searchTypes.product')
    },
    {
      searchType: EnumSettlementSearchType.InvoiceCode,
      label: t('settlements:components.searchFilterCard.searchTypes.invoiceCode'),
      visible: cycleState === EnumEstadoCiclo.PdteValidarFacturas
    }
  ]

  // #region Hooks Starts

  useEffect(() => {
    advancedSearch &&
      getDescriptions(
        cycleState === EnumEstadoCiclo.PdteValidarFacturas || isRefacturacionOrAnuladora
          ? EnumDominioTarea.Factura
          : EnumDominioTarea.Liquidacion
      )
  }, [])

  const getDescriptions = useCallback(async taskDomain => {
    setIsLoadingDescriptions(true)
    await fetchObjectionsDescriptions(taskDomain)
      .then(response => {
        if (response.success && response.data) {
          setDescriptionsOptions(listObjDescriptionsItemMapper(response.data))
        } else {
          error(t('objectionsDataTable:fetchResponse.fetchObjectionsDescriptions.error'))
        }
      })
      .finally(() => {
        setIsLoadingDescriptions(false)
      })
  }, [])
  // #endregion Hooks Ends
  return (
    <CardWithTransition show={showCard} unmountOnExit={true} className={classes.card}>
      <Typography variant="h5" style={{ padding: '16px 0 16px 16px' }}>
        {t('settlements:components.searchFilterCard.title')}
      </Typography>
      <Box className={classes.chipContainer}>
        {searchTypeOptions
          .filter(option => option.visible !== false)
          .map(option => (
            <Chip
              key={option.searchType}
              label={option.label}
              color={searchFormData.searchType === option.searchType ? 'primary' : 'default'}
              onClick={() => handleSearchTypeChange(option.searchType)}
              disabled={option.disabled}
            />
          ))}
      </Box>
      <Box
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === 'Enter' && !isSearchDisabled()) {
            e.preventDefault()
            onSearch(searchFormData)
          }
        }}
      >
        <Form
          titleCustom={<></>}
          sections={customSearchFormSections}
          data={searchFormData}
          options={searchFormOptions}
        />
      </Box>
      <Box className={classes.footer}>
        <Button variant="text" color="secondary" size="small" onClick={onCancel}>
          {t('settlements:components.searchFilterCard.actions.cancel')}
        </Button>
        <Button variant="text" color="primary" size="small" onClick={handleCleanFilters}>
          {t('settlements:components.searchFilterCard.actions.clean')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          size="small"
          disabled={isSearchDisabled()}
          onClick={() => {
            onSearch(searchFormData)
          }}
        >
          {t('settlements:components.searchFilterCard.actions.search')}
        </Button>
      </Box>
    </CardWithTransition>
  )
}
